import React, { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  Container,
  Row,
  Col,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
} from "reactstrap"
import "./ProfileSettings.css"
// Switched from a local react-toastify ToastContainer/toast (plain top-right
// popups, styled differently from the rest of the app) to the shared
// toastr-based helper used everywhere else — dark/pink-branded, bottom-
// right, and manages its own container, so no JSX mount point is needed.
import { triggerToast } from "../../utils/toast"
import { userProfileApi } from "../../services/userProfileApi"
import { navigateToRegisterForUpgrade } from "../../utils/loginRedirect"
import profile from "../../assets/images/profile.png"

// Same "is this a guest session" check used everywhere else in the app
// (api_helper.js, OptimizedDashboard, gameDashboard, etc.) — a guest carries
// a real authToken/authUser (so it can read data like a logged-in user), just
// tagged with is_guest: true.
const getIsGuestUser = () => {
  try {
    const authUser = JSON.parse(localStorage.getItem('authUser'))
    return authUser?.data?.is_guest === true || authUser?.is_guest === true
  } catch (_) {
    return false
  }
}

const ProfileSettings = () => {
  const navigate = useNavigate()
  const isGuest = getIsGuestUser()
  const EyeIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12Z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="12" cy="12" r="3.5" stroke="currentColor" strokeWidth="1.6"/>
    </svg>
  )

  const EyeOffIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M17.94 17.94A10.58 10.58 0 0 1 12 19c-7 0-11-7-11-7a18.77 18.77 0 0 1 5.06-5.94M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a18.67 18.67 0 0 1-3.36 4.36" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M9.9 9.9A3.5 3.5 0 0 0 15 15" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M1 1l22 22" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
    </svg>
  )
  const [formData, setFormData] = useState({
    name: "",
    username: "",
    email: "",
    mobile: "",
    currentPassword: "",
    newPassword: ""
  })

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)

  useEffect(() => {
    // Guest accounts don't have a real backend profile to fetch (or the
    // request errors out for them), which is why the form used to render
    // completely blank for guests — nothing ever populated formData. Show
    // what we actually know about the guest session instead (its generated
    // username/email, both already stored locally at login), skip the
    // real-user profile API entirely, and leave fields the guest genuinely
    // doesn't have (mobile, password) empty rather than guessing.
    if (isGuest) {
      const guestUsername = localStorage.getItem('userName') || "guest"
      const guestEmail = localStorage.getItem('userEmail') || ""
      setFormData(prev => ({
        ...prev,
        name: "Guest",
        username: guestUsername,
        email: guestEmail,
        mobile: "",
      }))
      setLoading(false)
      return
    }

    const loadProfile = async () => {
      try {
        setLoading(true)
        setError("")
        const data = await userProfileApi.fetchProfileDetails?.() || await userProfileApi.fetchProfile()
        const storedName = localStorage.getItem('userName') || ""
        setFormData(prev => ({
          ...prev,
          name: (data && (data.name || data.username)) || storedName || "",
          username: (data && (data.username || data.name)) || "",
          email: (data && data.email) || "",
          mobile: (data && (data.mobile || data.phone)) || "",
        }))
      } catch (e) {
        setError(e?.message || "Failed to load profile")
      } finally {
        setLoading(false)
      }
    }
    loadProfile()
  }, [isGuest])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleUpdate = async () => {
      // Defense in depth: the Update button is disabled for guests, but
      // don't rely on that alone to keep a no-op guest account from hitting
      // the real update-profile/change-password endpoints.
      if (isGuest) return

      // Validate the password pair up front so a half-filled pair doesn't
      // silently skip the password change after the profile update succeeds.
      const hasCurrent = Boolean(formData.currentPassword)
      const hasNew = Boolean(formData.newPassword)
      if (hasNew && !hasCurrent) {
        const msg = "Current password is required to change your password"
        setError(msg)
        triggerToast(msg, "error")
        return
      }
      if (hasCurrent && !hasNew) {
        const msg = "Enter a new password to change your password"
        setError(msg)
        triggerToast(msg, "error")
        return
      }

      try {
      setSaving(true)
      setError("")
      setSuccess("")

      // Update basic profile fields if present
      const updateRes = await userProfileApi.updateProfile({
        name: formData.name,
        mobile: formData.mobile,
        // backend may ignore username; included if supported later
      })
      const successMessages = []
      if (updateRes?.message) successMessages.push(updateRes.message)

      // Optionally change password if provided
      if (formData.currentPassword && formData.newPassword) {
        try {
          const pwRes = await userProfileApi.changePassword({
            current_password: formData.currentPassword,
            new_password: formData.newPassword,
          })
          if (pwRes?.message) successMessages.push(pwRes.message)
        } catch (pwErr) {
          // Surface specific error from API for incorrect current password
          setError(pwErr?.message || "Failed to change password")
          triggerToast(pwErr?.message || "Failed to change password", "error")
          return
        }
      }
      if (successMessages.length > 0) triggerToast(successMessages.join(" · "), "success")
    } catch (e) {
      setError(e?.message || "Failed to update profile")
      triggerToast(e?.message || "Failed to update profile", "error")
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    setFormData(prev => ({ ...prev, currentPassword: "", newPassword: "" }))
  }

  // Reuses the same "guest wants to sign up for real" flow as every other
  // upgrade/unlock CTA in the app — clears the guest session first (so
  // GuestMiddleware doesn't bounce /register back to /dashboard) then sends
  // them to the real sign-up form.
  const handleCreateAccount = () => {
    navigateToRegisterForUpgrade(navigate, true)
  }

  return (
    <React.Fragment>
      <div className="container-fluid" style={{padding:"32px" , maxWidth:"100vw"}}>
      <div className="page-content px-0">
      <div className="settings-header">
              <div className="header-icon me-0">
               <img src={profile} alt=""/>
              </div>
              <h1 className="settings-title">Profile</h1>
            </div>
         <div className=" px-0 mob_padd_off">
          {/* Form Section */}
          <div className="settings-form-container">
            {/* Header Section */}
            <Form>
              {/* Inline alerts removed; using toast notifications instead */}

              {/* Guest accounts get a real profile page (matching this page's
                  own "don't just hide the form" ask) but nothing below is
                  editable — there's no real account to save changes to. */}
              {isGuest && !loading && (
                <div className="guest-mode-banner">
                  <div className="guest-mode-banner-icon" aria-hidden="true">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <rect x="5" y="10" width="14" height="10" rx="2" stroke="#F6255A" strokeWidth="1.6" />
                      <path d="M8 10V7a4 4 0 0 1 8 0v3" stroke="#F6255A" strokeWidth="1.6" strokeLinecap="round" />
                    </svg>
                  </div>
                  <div className="guest-mode-banner-text">
                    <div className="guest-mode-banner-title">You're browsing as a guest</div>
                    <p>
                      These fields show what we know about your guest session, but a guest
                      account can't save changes — there's no name, mobile number, or password
                      on file to update. Create a free account to unlock editing.
                    </p>
                  </div>
                  <Button
                    color="primary"
                    className="guest-mode-banner-cta"
                    onClick={handleCreateAccount}
                  >
                    Create free account
                  </Button>
                </div>
              )}

              {loading ? (
                <div className="skeleton-form">
                  <div className="skeleton-label" />
                  <div className="skeleton-input" />
                  <div className="skeleton-label" />
                  <div className="skeleton-input" />
                  <div className="skeleton-label" />
                  <div className="skeleton-input" />
                  <div className="skeleton-label" />
                  <div className="skeleton-input" />
                  <div className="skeleton-label" />
                  <div className="skeleton-input" />
                  <div className="skeleton-actions">
                    <div className="skeleton-btn" />
                    <div className="skeleton-btn alt" />
                  </div>
                </div>
              ) : (
              <>
              {/* Guest accounts have nothing real to edit here — dim the
                  fields and block interaction, but keep them visible (with
                  whatever we do know, e.g. the guest's email) rather than
                  hiding the form outright. */}
              <div className={isGuest ? "guest-locked-fields" : undefined}>
              {/* Name Field */}
              <FormGroup className="form-field">
                <Label for="name" className="field-label">Name</Label>
                <Input
                  type="text"
                  name="name"
                  id="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="form-input"
                  disabled={isGuest}
                />
              </FormGroup>

              {/* Username Field */}
              <FormGroup className="form-field">
                <Label for="username" className="field-label">Username</Label>
                <Input
                  type="text"
                  name="username"
                  id="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  className="form-input"
                  disabled={isGuest}
                />
              </FormGroup>

              {/* Email Field */}
              <FormGroup className="form-field">
                <Label for="email" className="field-label">Email id</Label>
                <Input
                  type="email"
                  name="email"
                  id="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="form-input"
                  disabled={isGuest}
                />
              </FormGroup>

              {/* Mobile Field (optional) */}
              <FormGroup className="form-field">
                <Label for="mobile" className="field-label">Mobile Number</Label>
                {isGuest && <p className="helper-text">Not available on a guest account</p>}
                <Input
                  type="text"
                  name="mobile"
                  id="mobile"
                  placeholder={isGuest ? "Not available" : undefined}
                  value={formData.mobile}
                  onChange={handleInputChange}
                  className="form-input"
                  disabled={isGuest}
                />
              </FormGroup>

              {/* Current Password Field */}
              <FormGroup className="form-field">
                <Label for="currentPassword" className="field-label">Current Password</Label>
                <p className="helper-text">
                  {isGuest
                    ? "Not available on a guest account"
                    : "(we need your current password to confirm your changes)"}
                </p>
                <div className="password-field-wrapper">
                  <Input
                    type={showCurrentPassword ? "text" : "password"}
                    name="currentPassword"
                    id="currentPassword"
                    value={formData.currentPassword}
                    onChange={handleInputChange}
                    className="form-input password-input"
                    disabled={isGuest}
                  />
                  <button
                    type="button"
                    className="password-toggle-icon"
                    onClick={() => setShowCurrentPassword(v => !v)}
                    aria-label={showCurrentPassword ? "Hide password" : "Show password"}
                    disabled={isGuest}
                  >
                    {showCurrentPassword ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
              </FormGroup>

              {/* New Password Field */}
              <FormGroup className="form-field">
                <Label for="newPassword" className="field-label">New Password</Label>
                <p className="helper-text">
                  {isGuest
                    ? "Not available on a guest account"
                    : "(leave blank if you don't want to change it)"}
                </p>
                <div className="password-field-wrapper">
                  <Input
                    type={showNewPassword ? "text" : "password"}
                    name="newPassword"
                    id="newPassword"
                    value={formData.newPassword}
                    onChange={handleInputChange}
                    className="form-input password-input"
                    disabled={isGuest}
                  />
                  <button
                    type="button"
                    className="password-toggle-icon"
                    onClick={() => setShowNewPassword(v => !v)}
                    aria-label={showNewPassword ? "Hide password" : "Show password"}
                    disabled={isGuest}
                  >
                    {showNewPassword ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
              </FormGroup>
              </div>

              {/* Action Buttons */}
              <div className="action-buttons">
                <Button
                  color="primary"
                  className="update-btn"
                  onClick={handleUpdate}
                  disabled={saving || isGuest}
                  title={isGuest ? "Create a free account to save profile changes" : undefined}
                >
                  {saving ? 'Saving…' : 'Update'}
                </Button>
                <Button
                  color="secondary"
                  className="cancel-btn"
                  onClick={handleCancel}
                  disabled={saving}
                >
                  Cancel
                </Button>
              </div>
              </>
              )}
            </Form>
          </div>
       </div>
      </div>
      </div>
    </React.Fragment>
  )
}

export default ProfileSettings 